import { ChevronIcon } from 'assets/icons';
import { useRef } from 'react';
import { FolderInput } from 'common/import-source-tiles';
import {
  ComputerMark,
  DriveMark,
  DropboxMark,
  IcloudMark,
} from 'common/import-source-tiles/marks';
import styles from '../vault-import.module.scss';

interface Props {
  onSelectGoogleDrive: () => void;
  onSelectDropbox: () => void;
  onSelectICloud: () => void;
  onSelectLocalFolder: (files: File[]) => void;
  onBack: () => void;
}

type SourceCard = {
  key: string;
  title: string;
  subtitle: string;
  badge?: string;
  mark: React.ReactNode;
  iconClass: string;
  onClick: () => void;
};

export const ImportFromSource = ({
  onSelectGoogleDrive,
  onSelectDropbox,
  onSelectICloud,
  onSelectLocalFolder,
  onBack,
}: Props) => {
  const folderInputRef = useRef<HTMLInputElement | null>(null);

  const triggerFolderPicker = () => {
    folderInputRef.current?.click();
  };

  const cards: SourceCard[] = [
    {
      key: 'computer',
      title: 'Your Computer',
      subtitle: 'Pick a folder — subfolders become albums',
      mark: <ComputerMark />,
      iconClass: styles.sourceCardIconComputer,
      onClick: triggerFolderPicker,
    },
    {
      key: 'drive',
      title: 'Google Drive',
      subtitle: 'Pick photos, videos and documents from your Drive',
      mark: <DriveMark />,
      iconClass: styles.sourceCardIconDrive,
      onClick: onSelectGoogleDrive,
    },
    {
      key: 'dropbox',
      title: 'Dropbox',
      subtitle: 'Browse your Dropbox and import folders as albums',
      mark: <DropboxMark />,
      iconClass: styles.sourceCardIconDropbox,
      onClick: onSelectDropbox,
    },
    {
      key: 'icloud',
      title: 'iCloud',
      subtitle: 'Paste a Shared Album link — imports as one album',
      badge: 'Works from iPhone',
      mark: <IcloudMark />,
      iconClass: styles.sourceCardIconIcloud,
      onClick: onSelectICloud,
    },
  ];

  return (
    <>
      <div className={styles.headerBar}>
        <button
          type="button"
          aria-label="Back"
          className={styles.backBtn}
          onClick={onBack}
        >
          <ChevronIcon className={styles.backIcon} />
        </button>
        <h1 className={styles.headerTitle}>Bulk Upload</h1>
        <span className={styles.headerSpacer} aria-hidden />
      </div>
      <div className={styles.body}>
        <div className={styles.sourceHero}>
          <span className={styles.sourceHeroTitle}>
            Where are your memories?
          </span>
          <p className={styles.sourceHeroSubtitle}>
            Bring whole folders of photos, videos and documents into your vault
            — organized into albums automatically.
          </p>
        </div>
        <div className={styles.sourceGrid}>
          {cards.map((card) => (
            <button
              key={card.key}
              type="button"
              className={styles.sourceCard}
              onClick={card.onClick}
            >
              {card.badge && (
                <span className={styles.sourceCardBadge}>{card.badge}</span>
              )}
              <span
                className={`${styles.sourceCardIcon} ${card.iconClass}`}
                aria-hidden
              >
                {card.mark}
              </span>
              <span className={styles.sourceCardTitle}>{card.title}</span>
              <span className={styles.sourceCardSubtitle}>{card.subtitle}</span>
            </button>
          ))}
        </div>
        <FolderInput inputRef={folderInputRef} onPick={onSelectLocalFolder} />
      </div>
    </>
  );
};
